/**
 * A typed LevelLoad API client.
 *
 * Phase 1 hand-wrote one method per route. That worked at six operations. Phase 2 shipped
 * 73 operations across 52 paths, and hand-writing methods stopped being a maintenance
 * strategy and started being a drift generator: a wrapper is either regenerated in lockstep
 * with every route added to the API, or it quietly falls behind and lies about what the
 * server can do. A client covering 8% of the surface is worse than no client, because it
 * *looks* complete.
 *
 * So this file does not enumerate operations. It exposes one generic, fully typed entry
 * point -- `call(operationId, { path, query, body })` -- driven entirely by the `operations`
 * interface in `types.ts`, which is generated from `openapi.json`. Path params, query
 * params, the request body and the response shape are all inferred from the operation id
 * you pass, so:
 *
 *   - calling an operation id that doesn't exist is a compile error;
 *   - omitting a required path param, or passing a query param the operation doesn't
 *     declare, is a compile error;
 *   - reading a field that isn't in the response type is a compile error.
 *
 * Adding a route to the API means regenerating `types.ts` and adding one line to the small
 * runtime `REGISTRY` below (method + path template -- `call` cannot know either of those from
 * types alone, since TypeScript types don't exist at runtime). Nothing else in this file
 * needs to change, ever, for a new route to be fully typed and callable.
 *
 * No runtime dependencies. Drop this file and `types.ts` into the app and import from here.
 */

import type { components, operations, paths } from "./types";

/* ------------------------------------------------------------------ operation plumbing */

export type OperationId = keyof operations;

type Operation<Id extends OperationId> = operations[Id];

type HttpMethod = "GET" | "PUT" | "POST" | "PATCH" | "DELETE";

/** Runtime companion to `operations`: the one thing the generated types cannot carry. */
interface RouteInfo {
  method: HttpMethod;
  /** e.g. "/app/modes/{mode_id}" -- substituted against `path` params at call time. */
  path: string;
  /** Omitted means JSON, the overwhelming default. Only uploads are multipart today. */
  requestContentType?: "multipart/form-data";
}

/**
 * One entry per operation in `operations`. This is data, not logic: method, path template,
 * and (rarely) a non-JSON request content type. Regenerated by reading `paths` in
 * `types.ts` -- see the note at the top of the file. Keeping it in sync when a route is
 * added is a one-line addition, not a bespoke method with its own hand-typed signature.
 */
const REGISTRY: { [Id in OperationId]: RouteInfo } = {
  // Tenant discovery and selection (F-D3). Both live outside /app/*, because both have to
  // answer before a tenant is resolved: `discoverTenants` is unauthenticated and tells the
  // sign-in page which identity provider to use, `getMyTenants` runs after a verified token and
  // is the authoritative picker -- it is what tells the client which `X-LL-Tenant` to send.
  discoverTenants: { method: "POST", path: "/public/tenants/discover" },
  getMyTenants: { method: "GET", path: "/me/tenants" },
  acceptMyInvitations: { method: "POST", path: "/me/invitations/accept" },
  // Bootstrap
  getBootstrap: { method: "GET", path: "/app/bootstrap" },
  // Modes
  listModes: { method: "GET", path: "/app/modes" },
  createMode: { method: "POST", path: "/app/modes" },
  getMode: { method: "GET", path: "/app/modes/{mode_id}" },
  updateMode: { method: "PUT", path: "/app/modes/{mode_id}" },
  deleteMode: { method: "DELETE", path: "/app/modes/{mode_id}" },
  exportModes: { method: "POST", path: "/app/modes/exports" },
  // Carriers
  listCarriers: { method: "GET", path: "/app/carriers" },
  createCarrier: { method: "POST", path: "/app/carriers" },
  getCarrier: { method: "GET", path: "/app/carriers/{carrier_id}" },
  updateCarrier: { method: "PUT", path: "/app/carriers/{carrier_id}" },
  deleteCarrier: { method: "DELETE", path: "/app/carriers/{carrier_id}" },
  exportCarriers: { method: "POST", path: "/app/carriers/exports" },
  // Sites
  listSites: { method: "GET", path: "/app/sites" },
  createSite: { method: "POST", path: "/app/sites" },
  getSite: { method: "GET", path: "/app/sites/{site_id}" },
  updateSite: { method: "PUT", path: "/app/sites/{site_id}" },
  deleteSite: { method: "DELETE", path: "/app/sites/{site_id}" },
  exportSites: { method: "POST", path: "/app/sites/exports" },
  // Products
  listProducts: { method: "GET", path: "/app/products" },
  createProduct: { method: "POST", path: "/app/products" },
  getProduct: { method: "GET", path: "/app/products/{product_id}" },
  updateProduct: { method: "PUT", path: "/app/products/{product_id}" },
  deleteProduct: { method: "DELETE", path: "/app/products/{product_id}" },
  exportProducts: { method: "POST", path: "/app/products/exports" },
  // Lane capacities
  listLaneCapacities: { method: "GET", path: "/app/lane-capacities" },
  createLaneCapacity: { method: "POST", path: "/app/lane-capacities" },
  getLaneCapacity: { method: "GET", path: "/app/lane-capacities/{lane_capacity_id}" },
  updateLaneCapacity: { method: "PUT", path: "/app/lane-capacities/{lane_capacity_id}" },
  deleteLaneCapacity: { method: "DELETE", path: "/app/lane-capacities/{lane_capacity_id}" },
  exportLaneCapacities: { method: "POST", path: "/app/lane-capacities/exports" },
  // Rate versions (S1-D23, PM-D21): a lane capacity's dated rate history. The write permission is
  // the lane capacity's own (`lane-capacities:write`).
  listLaneRates: { method: "GET", path: "/app/lane-capacities/{lane_capacity_id}/rates" },
  addLaneRateVersion: { method: "POST", path: "/app/lane-capacities/{lane_capacity_id}/rates" },
  setLaneRateVersionEnd: {
    method: "PATCH",
    path: "/app/lane-capacities/{lane_capacity_id}/rates/{lane_rate_id}",
  },
  // Exchange rates (AN-D17, PM-D6): explicit pairs, dated, immutable but for their end.
  listFxRates: { method: "GET", path: "/app/fx-rates" },
  getFxRate: { method: "GET", path: "/app/fx-rates/{fx_rate_id}" },
  createFxRate: { method: "POST", path: "/app/fx-rates" },
  setFxRateEnd: { method: "PATCH", path: "/app/fx-rates/{fx_rate_id}" },
  // Calendars (read-only -- closures arrive through ingest, never through the app)
  listCalendars: { method: "GET", path: "/app/calendars" },
  exportCalendars: { method: "POST", path: "/app/calendars/exports" },
  // Priority policies
  listPriorityPolicies: { method: "GET", path: "/app/priority-policies" },
  createPriorityPolicy: { method: "POST", path: "/app/priority-policies" },
  getPriorityPolicy: { method: "GET", path: "/app/priority-policies/{policy_id}" },
  updatePriorityPolicy: { method: "PUT", path: "/app/priority-policies/{policy_id}" },
  deletePriorityPolicy: { method: "DELETE", path: "/app/priority-policies/{policy_id}" },
  exportPriorityPolicies: { method: "POST", path: "/app/priority-policies/exports" },
  // Solver configuration (tenant-wide singleton -- no id, no list)
  getSolverConfiguration: { method: "GET", path: "/app/solver-configuration" },
  updateSolverConfiguration: { method: "PUT", path: "/app/solver-configuration" },
  // Uploads (one generic pipeline shared by sites/products/lane-capacities -- API-CONTRACT.md §2.2)
  createUpload: { method: "POST", path: "/app/uploads", requestContentType: "multipart/form-data" },
  listUploads: { method: "GET", path: "/app/uploads" },
  getUpload: { method: "GET", path: "/app/uploads/{upload_id}" },
  applyUpload: { method: "POST", path: "/app/uploads/{upload_id}/apply" },
  getUploadTemplate: { method: "GET", path: "/app/uploads/templates/{resource}" },
  exportUploadIssues: { method: "POST", path: "/app/uploads/{upload_id}/issues/exports" },
  // Deliveries
  listDeliveries: { method: "GET", path: "/app/deliveries" },
  getDelivery: { method: "GET", path: "/app/deliveries/{delivery_id}" },
  exportDeliveryPayload: { method: "POST", path: "/app/deliveries/{delivery_id}/payload/exports" },
  exportDeliveryIssues: { method: "POST", path: "/app/deliveries/issues/exports" },
  // Core's neutral feed ingress (S1-D16): one route per core kind, for a sending system. The SPA
  // does not call these; they are here because REGISTRY is exact over `OperationId`.
  submitTransferRequestFeed: { method: "POST", path: "/app/feeds/transfer_request" },
  submitBookedLoadFeed: { method: "POST", path: "/app/feeds/booked_load" },
  submitReservedCapacityFeed: { method: "POST", path: "/app/feeds/reserved_capacity" },
  submitSiteClosureFeed: { method: "POST", path: "/app/feeds/site_closure" },
  submitProductFeed: { method: "POST", path: "/app/feeds/product" },
  submitLaneCapacityFeed: { method: "POST", path: "/app/feeds/lane_capacity" },
  // Data provenance. The catalogue returns only the live half -- dataset identity and
  // navigation stay client-side -- and the feed issues are derived from ingest.delivery_issue
  // at query time rather than materialised.
  getDataCatalogue: { method: "GET", path: "/app/data/catalogue" },
  listDataIssues: { method: "GET", path: "/app/data/issues" },
  getDataIssue: { method: "GET", path: "/app/data/issues/{issue_id}" },
  // Audit events
  listAuditEvents: { method: "GET", path: "/app/audit-events" },
  // Demand (read-only -- written by ingest, never by the app)
  listTransferRequests: { method: "GET", path: "/app/transfer-requests" },
  listBookedLoads: { method: "GET", path: "/app/booked-loads" },
  listReservedCapacity: { method: "GET", path: "/app/reserved-capacity" },
  // Outbound (read-only core model; Phase 3's KCC module publishes into it)
  listOutboundChannels: { method: "GET", path: "/app/outbound-channels" },
  getOutboundSnapshot: { method: "GET", path: "/app/outbound-snapshots/{snapshot_id}" },
  // KCC sales workspace (phase-3-design.md §6): reads over feed_state-resolved current
  // sales-order/forecast values, the max()-reconciled comparison view, in-app temporary
  // overrides (P3K-D2), and the frozen, run-scoped view distinct from "latest".
  listKccSalesOrders: { method: "GET", path: "/app/extensions/kcc/sales-orders" },
  listKccSalesForecasts: { method: "GET", path: "/app/extensions/kcc/sales-forecasts" },
  listKccSalesPlanning: { method: "GET", path: "/app/extensions/kcc/sales-planning" },
  overrideKccSalesOrder: { method: "POST", path: "/app/extensions/kcc/sales-orders/override" },
  overrideKccSalesForecast: { method: "POST", path: "/app/extensions/kcc/sales-forecasts/override" },
  revokeKccSalesOrderOverride: { method: "POST", path: "/app/extensions/kcc/sales-orders/override/revoke" },
  listKccSalesOrderOverrides: { method: "GET", path: "/app/extensions/kcc/sales-orders/overrides" },
  listKccSalesForecastOverrides: { method: "GET", path: "/app/extensions/kcc/sales-forecasts/overrides" },
  revokeKccSalesForecastOverride: {
    method: "POST",
    path: "/app/extensions/kcc/sales-forecasts/override/revoke",
  },
  exportKccSalesOrders: { method: "POST", path: "/app/extensions/kcc/sales-orders/exports" },
  exportKccSalesForecasts: { method: "POST", path: "/app/extensions/kcc/sales-forecasts/exports" },
  exportKccSalesPlanning: { method: "POST", path: "/app/extensions/kcc/sales-planning/exports" },
  getKccRunSalesOutbound: {
    method: "GET",
    path: "/app/extensions/kcc/planning-runs/{run_id}/outbound",
  },
  // Planning scopes (PS-D10..PS-D13). Reads need `runs:read`; the writes `planning-scopes:write`.
  listRoles: { method: "GET", path: "/app/roles" },
  listMembers: { method: "GET", path: "/app/members" },
  createInvitation: { method: "POST", path: "/app/invitations" },
  revokeInvitation: { method: "DELETE", path: "/app/invitations/{invitation_id}" },
  updateMemberRole: { method: "PUT", path: "/app/members/{principal_id}" },
  removeMember: { method: "DELETE", path: "/app/members/{principal_id}" },
  listPlanningScopes: { method: "GET", path: "/app/planning/scopes" },
  createPlanningScope: { method: "POST", path: "/app/planning/scopes" },
  getPlanningScope: { method: "GET", path: "/app/planning/scopes/{planning_scope_id}" },
  updatePlanningScope: { method: "PATCH", path: "/app/planning/scopes/{planning_scope_id}" },
  deletePlanningScope: { method: "DELETE", path: "/app/planning/scopes/{planning_scope_id}" },
  // Scenarios (W4-2, SN-D15): fork, overlay, run, diff, promote. Compare is the existing
  // `comparePlanningRuns`. Reads need `scenarios:read`, edits and runs `scenarios:write`, and
  // promotion `scenarios:promote`.
  listScenarios: { method: "GET", path: "/app/planning/scenarios" },
  createScenario: { method: "POST", path: "/app/planning/scenarios" },
  getScenario: { method: "GET", path: "/app/planning/scenarios/{scenario_id}" },
  updateScenario: { method: "PATCH", path: "/app/planning/scenarios/{scenario_id}" },
  deleteScenario: { method: "DELETE", path: "/app/planning/scenarios/{scenario_id}" },
  listScenarioOverlayChanges: {
    method: "GET",
    path: "/app/planning/scenarios/{scenario_id}/overlay",
  },
  listScenarioEffectiveOverlay: {
    method: "GET",
    path: "/app/planning/scenarios/{scenario_id}/overlay/effective",
  },
  createScenarioOverlayChange: {
    method: "POST",
    path: "/app/planning/scenarios/{scenario_id}/overlay/changes",
  },
  updateScenarioOverlayChange: {
    method: "PUT",
    path: "/app/planning/scenarios/{scenario_id}/overlay/changes/{change_id}",
  },
  deleteScenarioOverlayChange: {
    method: "DELETE",
    path: "/app/planning/scenarios/{scenario_id}/overlay/changes/{change_id}",
  },
  createScenarioRun: { method: "POST", path: "/app/planning/scenarios/{scenario_id}/runs" },
  getScenarioInputDiff: { method: "GET", path: "/app/planning/scenarios/{scenario_id}/diff" },
  getScenarioPromotionPreflight: {
    method: "GET",
    path: "/app/planning/scenarios/{scenario_id}/promote/preflight",
  },
  promoteScenario: { method: "POST", path: "/app/planning/scenarios/{scenario_id}/promote" },
  rebaseScenario: { method: "POST", path: "/app/planning/scenarios/{scenario_id}/rebase" },
  // Planning runs (API-CONTRACT.md §2.3)
  listPlanningRuns: { method: "GET", path: "/app/planning/runs" },
  createPlanningRun: { method: "POST", path: "/app/planning/runs" },
  // Which run. These replace three client-side heuristics that each guessed from
  // `listPlanningRuns` and silently depended on the server's default ordering. The server
  // declares both above `{run_id}`, or a UUID path would swallow them.
  getCurrentPlanningRun: { method: "GET", path: "/app/planning/runs/current" },
  getActivePlanningRun: { method: "GET", path: "/app/planning/runs/active" },
  getPlanningRun: { method: "GET", path: "/app/planning/runs/{run_id}" },
  cancelPlanningRun: { method: "POST", path: "/app/planning/runs/{run_id}/cancel" },
  listPlanningRunOutbound: { method: "GET", path: "/app/planning/runs/{run_id}/outbound" },
  listPlanningRunTransferRequests: {
    method: "GET",
    path: "/app/planning/runs/{run_id}/transfer-requests",
  },
  listPlanningRunShipments: { method: "GET", path: "/app/planning/runs/{run_id}/shipments" },
  exportPlanningRunShipments: {
    method: "POST",
    path: "/app/planning/runs/{run_id}/shipments/exports",
  },
  // What the run actually consumed, and -- the reason this route exists -- what it refused.
  // `excludedRowCount` is read against `rowCount`: demand whose ship day fell below the
  // horizon used to be dropped with nothing recording it (timezone-architecture.md §6).
  listPlanningRunInputs: { method: "GET", path: "/app/planning/runs/{run_id}/inputs" },
  exportPlanningRunPackage: {
    method: "POST",
    path: "/app/planning/runs/{run_id}/package/exports",
  },
  // Planning issues: a run's findings, computed after ingest by `planning_runs.derive` from the
  // run's frozen input package. A succeeded run therefore answers 404 `derived-output-pending`
  // until they exist -- which the SPA renders as an empty state, not an error.
  listPlanningIssues: { method: "GET", path: "/app/planning/issues" },
  getPlanningIssue: { method: "GET", path: "/app/planning/issues/{issue_id}" },
  // Analytics (SPA-D9). Spend, savings and findings are computed off the request path by
  // `analytics.compute_run` and share the same pending/unavailable problem types. Compare and
  // explain read core.shipment instead, so they answer immediately and degrade field-by-field.
  getAnalyticsSpend: { method: "GET", path: "/app/analytics/spend" },
  getAnalyticsSavings: { method: "GET", path: "/app/analytics/savings" },
  listAnalyticsFindings: { method: "GET", path: "/app/analytics/findings" },
  setFindingDisposition: {
    method: "POST",
    path: "/app/analytics/findings/{finding_id}/disposition",
  },
  explainPlanningRun: { method: "GET", path: "/app/planning/runs/{run_id}/explain" },
  comparePlanningRuns: { method: "GET", path: "/app/planning/runs/compare" },
  // Reports (all scoped to a run, default the latest succeeded one)
  listLaneDetailReport: { method: "GET", path: "/app/reports/lane-detail" },
  exportLaneDetailReport: { method: "POST", path: "/app/reports/lane-detail/exports" },
  listProductDetailReport: { method: "GET", path: "/app/reports/product-detail" },
  exportProductDetailReport: { method: "POST", path: "/app/reports/product-detail/exports" },
  listInboundLaneDetailReport: { method: "GET", path: "/app/reports/inbound-lane-detail" },
  listLaneDaysDriftReport: { method: "GET", path: "/app/reports/lane-days-drift" },
  exportInboundLaneDetailReport: {
    method: "POST",
    path: "/app/reports/inbound-lane-detail/exports",
  },
  exportLaneDaysDriftReport: { method: "POST", path: "/app/reports/lane-days-drift/exports" },
  // Aggregated server-side because the register is server-paginated: the chart used to sum
  // only the rows currently on screen.
  getInboundLaneDetailByDay: { method: "GET", path: "/app/reports/inbound-lane-detail/by-day" },
  // Read-only, against a per-run DuckDB snapshot in Blob -- never the live tenant database.
  runSqlPreview: { method: "POST", path: "/app/reports/sql" },
  getNetworkMap: { method: "GET", path: "/app/reports/map/network" },
  getMapDetails: { method: "GET", path: "/app/reports/map/details" },
  // Queue (operational; not a planner-facing screen)
  getQueueStats: { method: "GET", path: "/app/queue/stats" },
  listDeadLetters: { method: "GET", path: "/app/queue/dead-letters" },
  replayDeadLetter: { method: "POST", path: "/app/queue/dead-letters/{dead_letter_id}/replay" },
};

/* ------------------------------------------------------------------ type-level extraction */

/** Path params an operation declares, or `undefined` if it has none. */
type PathParamsOf<Id extends OperationId> = Operation<Id>["parameters"]["path"];

/** Query params an operation declares, or `undefined` if it has none. All are optional today. */
type QueryParamsOf<Id extends OperationId> = Operation<Id>["parameters"]["query"];

/**
 * A multipart body's fields come out of openapi-typescript as `string` even for a file field
 * (the spec has no way to say "binary" once it's a TS type), so every `string` field also
 * accepts a `Blob`/`File`. This is a generic loosening, not a per-operation override: it
 * applies to `createUpload` today and to any future multipart operation automatically.
 */
type LoosenFileFields<T> = { [K in keyof T]: T[K] extends string ? T[K] | Blob : T[K] };

type ContentOf<Id extends OperationId> = Operation<Id> extends { requestBody: { content: infer C } }
  ? C
  : undefined;

/** The request body an operation accepts, or `undefined` if it takes none. */
type RequestBodyOf<Id extends OperationId> = ContentOf<Id> extends undefined
  ? undefined
  : ContentOf<Id> extends { "application/json": infer J }
    ? J
    : ContentOf<Id> extends { "multipart/form-data": infer M }
      ? LoosenFileFields<M>
      : never;

type SuccessStatus = 200 | 201 | 204;

/**
 * The response type for an operation's success status. Every operation in this API has
 * exactly one 2xx response (error responses like 422 are excluded here on purpose), whose
 * body is `application/json`, `text/csv` (only `getUploadTemplate` today), or absent (204).
 */
type ResponseOf<Id extends OperationId> = {
  [K in keyof Operation<Id>["responses"] & SuccessStatus]: Operation<Id>["responses"][K] extends {
    content: { "application/json": infer J };
  }
    ? J
    : Operation<Id>["responses"][K] extends { content: { "text/csv": infer C } }
      ? C
      : void;
}[keyof Operation<Id>["responses"] & SuccessStatus];

/** Arguments to `call` for one operation: only the parts that operation actually has. */
type CallArgs<Id extends OperationId> = (PathParamsOf<Id> extends undefined
  ? { path?: undefined }
  : { path: PathParamsOf<Id> }) &
  (QueryParamsOf<Id> extends undefined ? { query?: undefined } : { query?: QueryParamsOf<Id> }) &
  (RequestBodyOf<Id> extends undefined ? { body?: undefined } : { body: RequestBodyOf<Id> });

/** True when every field of `CallArgs<Id>` is optional, so the second `call` argument can be too. */
type ArgsAreOptional<Id extends OperationId> = Record<never, never> extends CallArgs<Id>
  ? true
  : false;

/* ------------------------------------------------------------------ model aliases */
/*
 * A handful of named aliases for the models used most often in UI code. This is deliberately
 * not exhaustive -- every schema in `components["schemas"]` is already reachable as
 * `components["schemas"]["Whatever"]`, and `call(...)` infers the right one per operation
 * without needing an alias at all. These exist for code that wants to name a type up front
 * (component props, form state) rather than derive it from a call site.
 */

export type Bootstrap = components["schemas"]["Bootstrap"];
/** One entry of `Bootstrap.planningScopes`: the scope and its planning rule (PS-D12). */
export type BootstrapPlanningScope = components["schemas"]["BootstrapPlanningScope"];
export type PlanningScope = components["schemas"]["PlanningScope"];
export type PlanningScopeCreate = components["schemas"]["PlanningScopeCreate"];
export type PlanningScopeUpdate = components["schemas"]["PlanningScopeUpdate"];
export type Mode = components["schemas"]["Mode"];
export type ModeCreate = components["schemas"]["ModeCreate"];
export type ModeUpdate = components["schemas"]["ModeUpdate"];
export type Carrier = components["schemas"]["Carrier"];
export type CarrierCreate = components["schemas"]["CarrierCreate"];
export type CarrierUpdate = components["schemas"]["CarrierUpdate"];
export type Site = components["schemas"]["Site"];
export type SiteCreate = components["schemas"]["SiteCreate"];
export type SiteUpdate = components["schemas"]["SiteUpdate"];
export type Product = components["schemas"]["Product"];
export type ProductCreate = components["schemas"]["ProductCreate"];
export type ProductUpdate = components["schemas"]["ProductUpdate"];
export type LaneCapacity = components["schemas"]["LaneCapacity"];
export type LaneCapacityCreate = components["schemas"]["LaneCapacityCreate"];
export type LaneCapacityUpdate = components["schemas"]["LaneCapacityUpdate"];
/** One dated rate version of a lane capacity (S1-D23): the planning-currency figure plus its source. */
export type LaneRate = components["schemas"]["LaneRate"];
export type LaneRateHistory = components["schemas"]["LaneRateHistory"];
export type LaneRateVersionCreate = components["schemas"]["LaneRateVersionCreate"];
export type LaneRateVersionEnd = components["schemas"]["LaneRateVersionEnd"];
export type LaneRateInput = components["schemas"]["LaneRateInput"];
export type FxRate = components["schemas"]["FxRate"];
export type FxRateCreate = components["schemas"]["FxRateCreate"];
export type FxRateEnd = components["schemas"]["FxRateEnd"];
export type PriorityPolicy = components["schemas"]["PriorityPolicy"];
export type SolverConfiguration = components["schemas"]["SolverConfiguration"];
export type Upload = components["schemas"]["Upload"];
export type UploadDetail = components["schemas"]["UploadDetail"];
export type UploadIssue = components["schemas"]["UploadIssue"];
export type PlanningRun = components["schemas"]["PlanningRun"];
export type PlanningRunCreate = components["schemas"]["PlanningRunCreate"];
export type Scenario = components["schemas"]["Scenario"];
export type ScenarioCreate = components["schemas"]["ScenarioCreate"];
export type ScenarioUpdate = components["schemas"]["ScenarioUpdate"];
export type OverlayChange = components["schemas"]["OverlayChange"];
export type EffectiveOverlayChange = components["schemas"]["EffectiveOverlayChange"];
export type OverlayChangeCreate = components["schemas"]["OverlayChangeCreate"];
export type OverlayChangeUpdate = components["schemas"]["OverlayChangeUpdate"];
export type ScenarioInputDiff = components["schemas"]["ScenarioInputDiff"];
export type InputDiffItem = components["schemas"]["InputDiffItem"];
export type PromotionPreflight = components["schemas"]["PromotionPreflight"];
export type DriftedRow = components["schemas"]["DriftedRow"];
export type PromotionRequest = components["schemas"]["PromotionRequest"];
export type PromotionResult = components["schemas"]["PromotionResult"];
export type ScenarioRebase = components["schemas"]["ScenarioRebase"];
export type Shipment = components["schemas"]["Shipment"];
export type Delivery = components["schemas"]["Delivery"];
export type DeliveryDetail = components["schemas"]["DeliveryDetail"];
export type AuditEvent = components["schemas"]["AuditEvent"];
export type DownloadLink = components["schemas"]["DownloadLink"];
export type QueueStats = components["schemas"]["QueueStatsResponse"];

/** Shape shared by every list response. Handy for a generic table component. */
export type Page<T> = { items: T[]; total: number; limit: number; offset: number };

/**
 * Every permission the API can require. Generated, not hand-derived: it is the element type of
 * `BootstrapUser.permissions` in `types.ts`, which `openapi-typescript` reads out of
 * `openapi.json`'s enum for that field -- itself generated from `app.auth.permissions.
 * Permission`, the one Python `Literal` that both `require(...)` and `BootstrapUser` are typed
 * against (see that module's docstring). Regenerating `types.ts` (`make contract`) after adding
 * a permission there updates this automatically; nothing here is copied by hand. Gate UI
 * affordances on these, from `bootstrap`.
 */
export type Permission = components["schemas"]["BootstrapUser"]["permissions"][number];

/* ------------------------------------------------------------------ errors */

/**
 * Every error from `/app/*` is RFC 9457 `application/problem+json`. One shape, always -- so
 * render it in one place rather than guessing per call site.
 */
export interface Problem {
  type: string;
  title: string;
  status: number;
  instance?: string;
  detail?: string;
  /** Present on 403: the permission the caller was missing. */
  requiredPermission?: Permission;
  /** Present on 422: per-field validation failures. */
  errors?: Array<{ loc: (string | number)[]; msg: string; type: string }>;
}

export class ApiError extends Error {
  readonly problem: Problem;
  readonly status: number;

  constructor(problem: Problem) {
    super(problem.detail ?? problem.title);
    this.name = "ApiError";
    this.problem = problem;
    this.status = problem.status;
  }

  /** 401: not signed in, or the token expired. Re-acquire a token and retry once. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** 403: signed in, but lacking `requiredPermission` in this tenant. Do not retry. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

/* ------------------------------------------------------------------ client */

export interface ClientOptions {
  /**
   * API origin. One origin serves every tenant (decision F-D3) -- this is *the* API, not a
   * tenant's API. It used to be the latter: the hostname selected the tenant, so two tenants
   * could not share an origin, which made one sign-in page for many tenants impossible.
   */
  baseUrl: string;
  /** Called before every request. Return a current access token; refresh inside if needed. */
  getToken: () => string | Promise<string>;
  /**
   * Called before every request. Return the selected tenant's key, or null before one is
   * chosen -- `/public/tenants/discover` and `/me/tenants` are both answerable without one, and
   * they are how the client finds out what to return here.
   *
   * Sent as `X-LL-Tenant`. It selects; it does not authorise. The API reads the principal's
   * membership in the named tenant on every request and refuses when there is none, so a wrong
   * value here is a 403, never someone else's data.
   */
  getTenantKey?: () => string | null;
  fetch?: typeof globalThis.fetch;
}

export class LevelLoadClient {
  constructor(private readonly options: ClientOptions) {}

  /**
   * Call any operation by id. Path params, query params, and the request body are inferred
   * from `operations[Id]`, and so is the response type -- see the file header. The second
   * argument is optional exactly when the operation needs neither `path`, `query`, nor `body`
   * (e.g. `call("getBootstrap")`, `call("exportModes")`).
   */
  call<Id extends OperationId>(
    operationId: Id,
    ...rest: ArgsAreOptional<Id> extends true ? [args?: CallArgs<Id>] : [args: CallArgs<Id>]
  ): Promise<ResponseOf<Id>> {
    const args = (rest[0] ?? {}) as Partial<CallArgs<Id>>;
    const route = REGISTRY[operationId];
    const path = substitutePath(route.path, args.path as Record<string, unknown> | undefined);
    return this.request<ResponseOf<Id>>(route.method, path, {
      query: args.query as Record<string, unknown> | undefined,
      body: args.body,
      requestContentType: route.requestContentType,
    });
  }

  /* -- transport ---------------------------------------------------- */

  private async request<T>(
    method: HttpMethod,
    path: string,
    init: {
      query?: Record<string, unknown>;
      body?: unknown;
      requestContentType?: "multipart/form-data";
    } = {},
  ): Promise<T> {
    const url = new URL(path, this.options.baseUrl);
    for (const [key, value] of Object.entries(init.query ?? {})) {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.set(key, String(value));
      }
    }

    const headers: Record<string, string> = {
      Authorization: `Bearer ${await this.options.getToken()}`,
    };
    const tenantKey = this.options.getTenantKey?.() ?? null;
    if (tenantKey) headers["X-LL-Tenant"] = tenantKey;
    let body: BodyInit | undefined;
    if (init.body !== undefined) {
      if (init.requestContentType === "multipart/form-data") {
        body = toFormData(init.body as Record<string, unknown>);
        // Deliberately no Content-Type header: the browser sets the multipart boundary.
      } else {
        headers["Content-Type"] = "application/json";
        body = JSON.stringify(init.body);
      }
    }

    const doFetch = this.options.fetch ?? globalThis.fetch;
    const response = await doFetch(url.toString(), { method, headers, body });

    if (response.status === 204) return undefined as T;

    if (!response.ok) {
      // The server always sends problem+json, but a proxy or gateway in front of it may not.
      let problem: Problem;
      try {
        problem = (await response.json()) as Problem;
      } catch {
        problem = { type: "about:blank", title: response.statusText, status: response.status };
      }
      throw new ApiError({ ...problem, status: problem.status ?? response.status });
    }

    // Almost everything is JSON. `getUploadTemplate` answers `text/csv` -- honour whatever the
    // server actually sent rather than assuming JSON and failing to parse a CSV body.
    const contentType = response.headers.get("content-type") ?? "";
    if (contentType.includes("application/json")) {
      return (await response.json()) as T;
    }
    return (await response.text()) as unknown as T;
  }
}

function substitutePath(template: string, params: Record<string, unknown> | undefined): string {
  return template.replace(/\{([^}]+)\}/g, (_match, name: string) => {
    const value = params?.[name];
    if (value === undefined || value === null) {
      throw new Error(`Missing path parameter "${name}" for ${template}`);
    }
    return encodeURIComponent(String(value));
  });
}

function toFormData(fields: Record<string, unknown>): FormData {
  const form = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    if (value === undefined) continue;
    form.append(key, value as string | Blob);
  }
  return form;
}

/**
 * Download an artifact returned by an `export*` call, or by `createPlanningRun`'s package
 * export -- any operation whose response is a `DownloadLink` (fifteen routes as of Phase 2;
 * see API-CONTRACT.md §1.5 and §2.1).
 *
 * Deliberately a separate function, because the rule it encodes is easy to get wrong: the
 * pre-signed URL carries its own authorisation, so sending the bearer token with it will make
 * the storage service reject the request. Fetch it directly -- `window.location.href`, a plain
 * `<a href>`, or a bare `fetch` with no headers -- never through `LevelLoadClient`. Do not
 * cache the URL; it expires within minutes (`link.expiresAt`), and a stale one is a 403 from
 * the storage service, not from this API.
 */
export function downloadUrlFor(link: DownloadLink): string {
  return link.url;
}

export type { components, operations, paths };
